import type { AxiosRequestConfig, AxiosResponse } from "axios"

/**
 * 基于 @repo/api-types(openapi-typescript 生成的 `paths`)的轻量类型化请求封装。
 *
 * - 运行时只透传到既有 axios service 实例(service2 全响应形态),不绕过
 *   拦截器、审批重放、merge/mock 等已有请求封装能力。
 * - schema 变更时只需重新生成 @repo/api-types,本文件保持稳定;
 *   这是逐步废弃 @repo/codegen 运行时 client 的替代消费入口。
 */

type HttpMethod = "get" | "post" | "put" | "patch" | "delete"

/** 过滤出声明了指定 method 的 path(openapi-typescript 对未实现 method 生成 `get?: never`) */
export type PathsWithMethod<Paths, M extends HttpMethod> = {
  [P in keyof Paths]: Paths[P] extends { [K in M]: any } ? P : never
}[keyof Paths]

type OperationOf<Paths, P extends keyof Paths, M extends HttpMethod> =
  Paths[P] extends Record<M, infer Op> ? NonNullable<Op> : never

type JsonBodyOf<Op> = Op extends { requestBody: { content: { "application/json": infer B } } }
  ? B
  : Op extends { requestBody?: { content: { "application/json": infer B } } }
    ? B | undefined
    : never

/** 取 200 响应体(后端统一为 R 包装的业务 envelope) */
type SuccessDataOf<Op> = Op extends { responses: { 200: { content: infer C } } }
  ? C[keyof C]
  : unknown

type PathParamsOf<Op> = Op extends { parameters: { path: infer P } } ? P : never

type QueryParamsOf<Op> = Op extends { parameters: { query: infer Q } }
  ? Q
  : Op extends { parameters: { query?: infer Q } }
    ? Q | undefined
    : never

export type TypedRequestOptions<Op> = {
  /** URL 查询参数;operation 未声明 query 时不允许传入 */
  query?: QueryParamsOf<Op>
  /** 透传 axios 配置,与 defaults 浅合并(headers 合并一层) */
  config?: AxiosRequestConfig
} & ([PathParamsOf<Op>] extends [never]
  ? { pathParams?: never }
  : { pathParams: PathParamsOf<Op> })

/** path 无参数时 options 整体可省略 */
type OptionsArg<Op> = [PathParamsOf<Op>] extends [never]
  ? [options?: TypedRequestOptions<Op>]
  : [options: TypedRequestOptions<Op>]

/** 最小请求实例面;AxiosInstance 天然满足,便于测试注入 mock */
export interface TypedHttpService {
  get: (url: string, config?: AxiosRequestConfig) => Promise<any>
  post: (url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<any>
  put: (url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<any>
  patch: (url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<any>
  delete: (url: string, config?: AxiosRequestConfig) => Promise<any>
}

export interface TypedClientOptions {
  /** 全响应形态的 service 实例(如 2b-fe 的 service2) */
  service: TypedHttpService
  /** 每次请求默认携带的 axios 配置,如 getConfig 的 baseURL/headers/timeout */
  defaults?: AxiosRequestConfig
}

function buildUrl(template: string, pathParams?: Record<string, unknown>) {
  return template.replace(/\{(\w+)\}/g, (_match, key: string) => {
    const value = pathParams?.[key]
    if (value === undefined || value === null) {
      throw new Error(`[typed-client] 缺少路径参数 ${key}: ${template}`)
    }
    return encodeURIComponent(String(value))
  })
}

export function createTypedClient<Paths>(clientOptions: TypedClientOptions) {
  const { service, defaults } = clientOptions

  function mergeConfig(query?: unknown, config?: AxiosRequestConfig): AxiosRequestConfig {
    const merged: AxiosRequestConfig = { ...defaults, ...config }
    if (defaults?.headers || config?.headers) {
      merged.headers = { ...defaults?.headers, ...config?.headers }
    }
    if (query !== undefined) {
      merged.params = config?.params ? { ...query, ...config.params } : query
    }
    return merged
  }

  return {
    get<P extends PathsWithMethod<Paths, "get">>(
      url: P,
      ...args: OptionsArg<OperationOf<Paths, P, "get">>
    ): Promise<AxiosResponse<SuccessDataOf<OperationOf<Paths, P, "get">>>> {
      const [options] = args
      return service.get(
        buildUrl(url as string, options?.pathParams as Record<string, unknown> | undefined),
        mergeConfig(options?.query, options?.config),
      )
    },
    post<P extends PathsWithMethod<Paths, "post">>(
      url: P,
      body: JsonBodyOf<OperationOf<Paths, P, "post">>,
      ...args: OptionsArg<OperationOf<Paths, P, "post">>
    ): Promise<AxiosResponse<SuccessDataOf<OperationOf<Paths, P, "post">>>> {
      const [options] = args
      return service.post(
        buildUrl(url as string, options?.pathParams as Record<string, unknown> | undefined),
        body,
        mergeConfig(options?.query, options?.config),
      )
    },
    put<P extends PathsWithMethod<Paths, "put">>(
      url: P,
      body: JsonBodyOf<OperationOf<Paths, P, "put">>,
      ...args: OptionsArg<OperationOf<Paths, P, "put">>
    ): Promise<AxiosResponse<SuccessDataOf<OperationOf<Paths, P, "put">>>> {
      const [options] = args
      return service.put(
        buildUrl(url as string, options?.pathParams as Record<string, unknown> | undefined),
        body,
        mergeConfig(options?.query, options?.config),
      )
    },
    patch<P extends PathsWithMethod<Paths, "patch">>(
      url: P,
      body: JsonBodyOf<OperationOf<Paths, P, "patch">>,
      ...args: OptionsArg<OperationOf<Paths, P, "patch">>
    ): Promise<AxiosResponse<SuccessDataOf<OperationOf<Paths, P, "patch">>>> {
      const [options] = args
      return service.patch(
        buildUrl(url as string, options?.pathParams as Record<string, unknown> | undefined),
        body,
        mergeConfig(options?.query, options?.config),
      )
    },
    delete<P extends PathsWithMethod<Paths, "delete">>(
      url: P,
      ...args: OptionsArg<OperationOf<Paths, P, "delete">>
    ): Promise<AxiosResponse<SuccessDataOf<OperationOf<Paths, P, "delete">>>> {
      const [options] = args
      return service.delete(
        buildUrl(url as string, options?.pathParams as Record<string, unknown> | undefined),
        mergeConfig(options?.query, options?.config),
      )
    },
  }
}
